import {
  Activity,
  CheckCircle,
  Clock,
  FileText,
  History,
  Home,
  Hourglass,
  Minus,
  TrendingUp,
  UserRound,
  Users,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { DashboardSectionLabel } from "@/shared/ui/DashboardSectionLabel";
import { adminKeyIndicators, adminQuickLinks } from "../../data/adminDashboard";
import { adminIndicatorRoutes } from "../../navigation/dashboardRoutes";
import { DashboardMetricCard } from "../dashboard/DashboardMetricCard";
import { KpiGrid, type KpiTone } from "../KpiCard";
import {
  DashboardQuickAccess,
  type DashboardQuickAccessConfig,
} from "../dashboard/DashboardQuickAccess";

const indicatorIcons = {
  file: FileText,
  clock: Clock,
  check: CheckCircle,
  home: Home,
};

const footerIcons = {
  minus: Minus,
  hourglass: Hourglass,
  trending: TrendingUp,
  activity: Activity,
};

const quickLinkIcons = {
  file: FileText,
  home: Home,
  users: Users,
  provider: UserRound,
  conventions: FileText,
  logs: History,
};

const adminMetricConfig: Record<string, KpiTone> = {
  totalInvoices: "sky",
  inReview: "amber",
  approved: "mint",
  admissions: "lime",
};

const adminQuickAccessConfig: Record<string, DashboardQuickAccessConfig> = {
  invoices: { icon: FileText, tone: "blue" },
  admissions: { icon: Home, tone: "green" },
  users: { icon: Users, tone: "orange" },
  providers: { icon: UserRound, tone: "purple" },
  conventions: { icon: FileText, tone: "mint" },
  logs: { icon: History, tone: "blue" },
};

export function AdminKeyIndicators({
  indicators = adminKeyIndicators,
}: {
  indicators?: Array<(typeof adminKeyIndicators)[number] & { value: number }>;
}) {
  const { t } = useTranslation();
  const labels = t.safecheck.adminDashboard.indicators;
  const footers = t.safecheck.adminDashboard.indicatorFooters;

  return (
    <KpiGrid className="grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
      {indicators.map((indicator) => {
        const Icon = indicatorIcons[indicator.icon];
        const FooterIcon = footerIcons[indicator.footerIcon];
        const label = labels[indicator.id as keyof typeof labels];
        let footer = footers[indicator.footerKey as keyof typeof footers];
        if (indicator.id === "admissions" && indicator.footerKey === "inProgress") {
          footer = footer.replace("{count}", String(indicator.value));
        }
        const tone = adminMetricConfig[indicator.id] ?? "slate";

        return (
          <DashboardMetricCard
            key={indicator.id}
            title={label}
            value={String(indicator.value)}
            footer={
              <span className="inline-flex items-center gap-1">
                <FooterIcon size={12} />
                {footer}
              </span>
            }
            icon={Icon}
            href={adminIndicatorRoutes[indicator.id as keyof typeof adminIndicatorRoutes]}
            tone={tone}
          />
        );
      })}
    </KpiGrid>
  );
}

export function AdminQuickLinks() {
  const { t } = useTranslation();
  const labels = t.safecheck.adminDashboard.quickLinks;

  const items = adminQuickLinks.map((link) => ({
    id: link.id,
    href: link.href,
    label: labels[link.id as keyof typeof labels],
  }));

  return (
    <DashboardQuickAccess
      items={items}
      configById={adminQuickAccessConfig}
      className="md:grid-cols-3 lg:grid-cols-6"
    />
  );
}

export function AdminDashboardSectionLabel({ children }: { children: React.ReactNode }) {
  return <DashboardSectionLabel>{children}</DashboardSectionLabel>;
}
